import { useState, type FormEvent } from 'react';
import { aboutData } from '@/mocks/about';

const formSubmitAddr = 'https://readdy.ai/api/form/d9snq394ne17ei0sdklg';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const rawHoneypot = formData.get('phone_alt');
    const honeypotValue = typeof rawHoneypot === 'string' ? rawHoneypot.trim() : '';
    if (honeypotValue) {
      setStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch(formSubmitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries()).map(([k, v]) => [
            k,
            typeof v === 'string' ? v : '',
          ])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // keep as raw text
      }

      const serverMsg: string =
        (parsed?.meta as Record<string, unknown>)?.message as string ||
        (parsed?.message as string) ||
        '';

      if (response.ok && (parsed?.code as string) === 'OK') {
        setStatus('success');
        form.reset();
      } else if (serverMsg.includes('spam') || (parsed?.code as string)?.includes('form data is spam')) {
        setErrorMsg(serverMsg || '提交失败，请稍后再试');
        setStatus('error');
      } else {
        setErrorMsg(serverMsg || '提交失败，请稍后再试');
        setStatus('error');
      }
    } catch {
      setErrorMsg('网络错误，请检查连接后重试');
      setStatus('error');
    }
  };

  return (
    <section className="py-10 md:py-16">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
            联系我们
          </h2>
          <p className="text-sm text-foreground-600">
            {aboutData.contact.response}
          </p>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
          <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  邮箱 <span className="text-red-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
            </div>

            <div>
              <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                主题
              </label>
              <input
                id="contact-subject"
                name="subject"
                type="text"
                className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                placeholder="请输入咨询主题"
              />
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                留言内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                placeholder="请输入您的留言（不超过500字）"
              ></textarea>
              <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
            </div>

            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="spam-field"
            />

            {status === 'success' && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-accent-50 text-accent-700 text-sm">
                <i className="ri-checkbox-circle-line"></i>
                感谢您的留言！我们会尽快回复。
              </div>
            )}

            {status === 'error' && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                <i className="ri-error-warning-line"></i>
                {errorMsg || '提交失败，请稍后重试'}
              </div>
            )}

            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-send-plane-line"></i>
              发送留言
            </button>
          </form>
        </div>

        <div className="mt-6 p-4 rounded-lg bg-background-100 border border-background-200/70">
          <p className="text-xs text-foreground-500 leading-relaxed">
            <strong className="text-foreground-700">免责声明：</strong>
            {aboutData.contact.disclaimer}
          </p>
        </div>
      </div>
    </section>
  );
}